import { useSelector } from 'react-redux';
import { useConfiguration } from '../../context/configurationContext';
import css from './CustomFooter.module.css';
import FOOTERLOGO from './img/AllergoWhiteLogo.png';

const ArrowRightIcon = () => (
  <svg className={css.svgIconSmall} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
  </svg>
);

const EmailIcon = () => (
  <svg className={css.svgIconSmall} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2" fill="none" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const CTASection = () => {
  const currentUser = useSelector(state => state.user?.currentUser);
  const isLawyer = currentUser?.attributes?.profile?.publicData?.userType === 'lawyer';
  return (
    <section className={css.ctaSection}>
      <div className={css.ctaContent}>
       {isLawyer ? 
        (<>
            <h2 className={css.ctaTitle}>
              Browse legal support requests to find new interesting clients
            </h2>
            <p className={css.ctaDescription}>
              Filter by jurisdiction, specialism and sector to find opportunities that match your expertise.        
            </p>
          </>) :
          (<>  
            <h2 className={css.ctaTitle}>
              Post your legal support request in under{' '}
              <span className={css.ctaTitleHighlight}>5 minutes</span>
            </h2>
            <p className={css.ctaDescription}>
              Let Ally guide you through the process. Get matched with qualified legal consultants and start receiving proposals today.
            </p>
          </>)
        }
        <div className={css.ctaButtons}>
          {/*<a href={isLawyer ? '/s' : '/ally'} className={css.ctaButtonPrimary}>
            {isLawyer ? 'View Job Opportunities' : 'Get Started as a Client'}
            <ArrowRightIcon />
          </a>*/}
          {(!isLawyer && !currentUser) ? (
            <>
              <a href="/signup" className={css.ctaButtonPrimary}>
                Join as Legal Consultant
              </a>

              <a href="/signup" className={css.heroButtonSecondary}>
                Get Started as a Client
              </a>
            </>
          ) : !isLawyer ? (
            <a href="/ally" className={css.ctaButtonPrimary}>
              Post your Legal Support Request
            </a>
          ) : (
            <a href="/s" className={css.ctaButtonPrimary}>
              View Job Opportunities
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

// Static design columns — used when the Console has no parseable navigation blocks.
const STATIC_COLUMNS = [
  {
    title: 'For Businesses',
    links: [
      { text: 'Post a Requirement', href: '/l/new' },
      { text: 'How It Works', href: '/p/how-it-works' },
      { text: 'Pricing', href: '/p/pricing' },
    ],
  },
  {
    title: 'For Lawyers',
    links: [
      { text: 'Join the Marketplace', href: '/signup' },
      { text: 'Browse Opportunities', href: '/s' },
      { text: 'Create Profile', href: '/profile-settings' },
    ],
  },
  {
    title: 'Company',
    links: [
      { text: 'About', href: '/p/about' },
      { text: 'Privacy Policy', href: '/privacy-policy' },
      { text: 'Terms of Service', href: '/terms-of-service' },
    ],
  },
];

// Parse a footerBlock's markdown text into a title + links array.
// Sharetribe Console stores block content as markdown, e.g.:
//   ### For Businesses
//   - [Post a Requirement](/l/new)
const parseMarkdownBlock = textContent => {
  if (!textContent) return { title: '', links: [] };
  const lines = textContent.split('\n').filter(l => l.trim());
  let title = '';
  const links = [];
  for (const line of lines) {
    const headingMatch = line.match(/^#+\s+(.+)$/);
    if (headingMatch) { title = headingMatch[1].trim(); continue; }
    const linkMatch = line.match(/^[-*]\s+\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) links.push({ text: linkMatch[1], href: linkMatch[2] });
  }
  return { title, links };
};

const CustomFooter = () => {
  const { footer = {} } = useConfiguration();

  const { slogan, copyright, socialMediaLinks = [], blocks = [] } = footer;

  const getSloganText = () => {
    if (!slogan) return 'Flexible Legal Talent Marketplace connecting scaling businesses and in-house legal teams with legal consultants';
    if (typeof slogan === 'string') return slogan;
    if (slogan?.content) return slogan.content;
    return 'Flexible Legal Talent Marketplace connecting scaling businesses and in-house legal teams with legal consultants';
  };

  const getCopyrightText = () => {
    if (!copyright) return `\u00A9 ${new Date().getFullYear()} Allegro Legal. All rights reserved.`;
    if (typeof copyright === 'string') return copyright;
    if (copyright?.content) return copyright.content;
    return `\u00A9 ${new Date().getFullYear()} Allegro Legal. All rights reserved.`;
  };

  const renderColumns = () => {
    if (blocks && blocks.length > 0) {
      const parsed = blocks.map(block => {
        const { title, links } = parseMarkdownBlock(block?.text?.content || '');
        return { blockId: block?.blockId, title: title || block?.blockName || '', links };
      });
      if (parsed.some(col => col.links.length > 0)) {
        return parsed.map(({ blockId, title, links }, index) => (
          <div key={blockId || index}>
            {title && <p className={css.footerColumnTitle}>{title}</p>}
            <ul className={css.footerLinks}>
              {links.map(link => (
                <li key={link.text}>
                  <a href={link.href} className={css.footerLink}>{link.text}</a>
                </li>
              ))}
              {index == 0 && renderSocialLinks()}
            </ul>
          </div>
        ));
      }
    }

    return STATIC_COLUMNS.map(col => (
      <div key={col.title}>
        <p className={css.footerColumnTitle}>{col.title}</p>
        <ul className={css.footerLinks}>
          {col.links.map(link => (
            <li key={link.text}>
              <a href={link.href} className={css.footerLink}>{link.text}</a>
            </li>
          ))}
        </ul>
      </div>
    ));
  };

  const renderSocialLinks = () => {
    if (!socialMediaLinks || socialMediaLinks.length === 0) return null;
    return (
      <div className={css.footerSocialLinks}>
        {socialMediaLinks.map((sml, idx) => {
          const platform = sml?.link?.platform || sml?.platform || '';
          const url = sml?.link?.url || sml?.url || '#';
          return (
            <a key={idx} href={url} className={css.footerSocialLink} target="_blank" rel="noopener noreferrer" aria-label={platform}>
              {platform}
            </a>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <CTASection />
      <footer className={css.customFooter}>
        <div className={css.footerContent}>
          <div className={css.footerGrid}>
            <div className={css.footerBrand}>
              <p className={css.footerLogo}>
                <img src={FOOTERLOGO} width='100' />
              </p>
              <p className={css.footerBrandDesc}>{getSloganText()}</p>
            </div>
            {renderColumns()}
            <div>
              <p className={css.footerColumnTitle}>Get in Touch</p>
              <a href="mailto:ally@allegro.law" className={css.getInTouchBtn}>
                <EmailIcon />
                ally@allegro.law
              </a>
            </div>
          </div>
          <hr className={css.footerDivider} />
          <div className={css.footerBottom}>
            <p className={css.footerCopyright}>{getCopyrightText()}</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default CustomFooter;
